import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router'
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import { useGetSearchParam, useSessionStorage } from '@/hooks'
import { ENDPOINTS, isSuperUser } from '@/util'

type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
}

export function ChatTestPage() {
  const [roles] = useSessionStorage('roles', 0)
  const [token] = useSessionStorage('token', '')

  const appKeyFromUrl = useGetSearchParam('appKey')
  const appKey = appKeyFromUrl

  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [connecting, setConnecting] = useState<boolean>(true)
  const [disconnectedError, setDisconnectedError] = useState<string>('')
  const [chatId, setChatId] = useState<string | null>(null)
  const [pendingUserInput, setPendingUserInput] = useState<string>('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const awaitingAssistantRef = useRef<boolean>(false)
  const requestIdRef = useRef<string>('')
  const requestAttemptRef = useRef<number>(0)
  const requestTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const hubUrl = `${ENDPOINTS.DOTNET}/rest/collaboration-hub`

  const generateRequestId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
  const isAllowed = isSuperUser(roles)
  const canConnect = useMemo(() => Boolean(token && appKey && isAllowed), [token, appKey, isAllowed])

  useEffect(() => {
    if (!canConnect) return

    const connectionBuilder = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)

    const newConnection = connectionBuilder.build()
    let didStart = false

    const clearPendingTimeout = () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current)
        requestTimeoutRef.current = null
      }
    }

    const ensureAssistantPlaceholder = () => {
      awaitingAssistantRef.current = true
      setMessages((prev) => {
        const last = prev[prev.length - 1]
        if (last && last.role === 'assistant' && last.content.length === 0) return prev
        return [...prev, { id: generateRequestId(), role: 'assistant', content: '' }]
      })
    }

    const scheduleRequestTimeout = (invokeRequest: () => void) => {
      clearPendingTimeout()
      requestTimeoutRef.current = setTimeout(() => {
        if (requestAttemptRef.current < 2) {
          // retry up to 3 total attempts (0,1,2)
          requestAttemptRef.current += 1
          requestIdRef.current = generateRequestId()
          invokeRequest()
          scheduleRequestTimeout(invokeRequest)
        } else {
          setDisconnectedError('Cannot connect (timeout)')
          setConnecting(false)
        }
      }, 10_000)
    }

    const handleChatCreated = (createdChatId: string, requestId: string) => {
      if (requestId !== requestIdRef.current) return
      setChatId(createdChatId)
      setConnecting(false)
      setDisconnectedError('')
      clearPendingTimeout()
      // Ensure a starting assistant message exists so early streamed tokens append
      ensureAssistantPlaceholder()
    }

    const handleNoListeners = (requestId: string) => {
      if (requestId !== requestIdRef.current) return
      setDisconnectedError('No listeners available for this app')
      setConnecting(false)
      clearPendingTimeout()
    }

    const handleReceiveMessage = (message: string) => {
      setMessages((prev) => {
        const last = prev[prev.length - 1]
        if (awaitingAssistantRef.current && last && last.role === 'assistant') {
          // Append to the existing assistant message for streaming effect
          const updated = prev.slice(0, prev.length - 1)
          updated.push({ ...last, content: last.content + message })
          return updated
        }
        // Create a new assistant message if none exists yet
        return [...prev, { id: generateRequestId(), role: 'assistant', content: message }]
      })
    }

    const handlePeerDisconnected = (disconnectedChatId: string) => {
      if (chatId && disconnectedChatId !== chatId) return
      setDisconnectedError('Peer disconnected')
    }

    newConnection.on('ChatCreated', handleChatCreated)
    newConnection.on('NoListeners', handleNoListeners)
    newConnection.on('ReceiveMessage', handleReceiveMessage)
    newConnection.on('PeerDisconnected', handlePeerDisconnected)

    const startAsync = async () => {
      try {
        setConnecting(true)
        await newConnection.start()
        didStart = true
        setConnection(newConnection)
        // Join app channel then request a 1-to-1 chat with timeout/retry
        await newConnection.invoke('JoinAppKeyChannel', appKey, 'frontend')
        requestAttemptRef.current = 0
        requestIdRef.current = generateRequestId()
        const invokeRequest = () => newConnection.invoke('RequestChat', appKey, requestIdRef.current, -1)
        // Create a starting assistant message so the first tokens append even if ChatCreated arrives later
        ensureAssistantPlaceholder()
        await invokeRequest()
        scheduleRequestTimeout(invokeRequest)
      } catch (err) {
        console.error('Failed to start SignalR connection', err)
        setDisconnectedError('Failed to connect')
        setConnecting(false)
      }
    }

    newConnection.onreconnected(async () => {
      try {
        if (!appKey) return
        await newConnection.invoke('JoinAppKeyChannel', appKey, 'frontend')
        requestAttemptRef.current = 0
        requestIdRef.current = generateRequestId()
        const invokeRequest = () => newConnection.invoke('RequestChat', appKey, requestIdRef.current, -1)
        ensureAssistantPlaceholder()
        await invokeRequest()
        scheduleRequestTimeout(invokeRequest)
      } catch (err) {
        console.error('Failed to re-establish chat after reconnect', err)
        setDisconnectedError('Reconnection error')
      }
    })

    startAsync()

    return () => {
      newConnection.off('ChatCreated', handleChatCreated)
      newConnection.off('NoListeners', handleNoListeners)
      newConnection.off('ReceiveMessage', handleReceiveMessage)
      newConnection.off('PeerDisconnected', handlePeerDisconnected)
      if (requestTimeoutRef.current) clearTimeout(requestTimeoutRef.current)
      // Avoid calling stop() if start() never completed; this prevents
      // 'stopped during negotiation' under React StrictMode double-invoke
      if (didStart && newConnection.state !== HubConnectionState.Disconnected) {
        newConnection.stop().catch(() => {})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canConnect, appKey])

  useEffect(() => {
    // Auto-scroll to bottom on new messages
    if (!scrollContainerRef.current) return
    scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
  }, [messages])

  const onSubmitUserMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pendingUserInput.trim()) return
    if (!connection || connection.state !== HubConnectionState.Connected) return
    if (!chatId) return

    const userText = pendingUserInput
    setPendingUserInput('')
    setMessages((prev) => [...prev, { id: generateRequestId(), role: 'user', content: userText }])

    // Prepare an assistant placeholder to stream into
    awaitingAssistantRef.current = true
    setMessages((prev) => [...prev, { id: generateRequestId(), role: 'assistant', content: '' }])

    try {
      await connection.invoke('SendMessageToChat', chatId, userText)
    } catch (err) {
      console.error('Failed to send message', err)
      setDisconnectedError('Failed to send message')
      awaitingAssistantRef.current = false
    }
  }

  // Stop streaming flag when a small idle period passes after last token (simple heuristic)
  useEffect(() => {
    if (!awaitingAssistantRef.current) return
    const timer = setTimeout(() => {
      awaitingAssistantRef.current = false
    }, 600)
    return () => clearTimeout(timer)
  }, [messages])

  if (!isAllowed) {
    return <Navigate to="/" replace />
  }

  if (!appKey) {
    return (
      <div className="flex size-full items-center justify-center">
        <div className="flex max-w-xl flex-col items-center gap-y-4 text-center">
          <div className="text-lg font-semibold">Missing appKey</div>
          <div className="text-sm text-muted-foreground">Add ?appKey=YOUR_APP_KEY to the URL and reload.</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-120px)] flex-col">
      {/* Status banners */}
      {connecting && (
        <div className="mx-auto my-2 rounded-md border border-border bg-muted px-3 py-2 text-sm">Connecting…</div>
      )}
      {!!disconnectedError && (
        <div className="mx-auto my-2 rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {disconnectedError}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto px-4 py-3">
        <div className="mx-auto flex max-w-3xl flex-col gap-y-4">
          {messages.map((m) => (
            <div key={m.id} className={m.role === 'user' ? 'text-right' : 'text-left'}>
              <div
                className={
                  'inline-block whitespace-pre-wrap rounded-lg px-3 py-2 ' +
                  (m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground')
                }
              >
                {m.content || (m.role === 'assistant' && awaitingAssistantRef.current ? '…' : '')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Input */}
      <form onSubmit={onSubmitUserMessage} className="border-t border-border px-4 py-3">
        <div className="mx-auto flex max-w-3xl items-end gap-2">
          <textarea
            className="bg-offwhite min-h-[48px] flex-1 resize-none rounded-md border border-input px-3 py-2 text-sm shadow-sm focus:outline-none"
            placeholder="Type your message"
            value={pendingUserInput}
            onChange={(e) => setPendingUserInput(e.target.value)}
            disabled={!chatId || connecting || !!disconnectedError}
          />
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            disabled={!pendingUserInput.trim() || !chatId || connecting || !!disconnectedError}
          >
            Send
          </button>
        </div>
      </form>
    </div>
  )
}
